export const ACCOUNT_TYPES = [
  { value: 'PERSONAL_CHECKING', label: 'Personal checking' },
  { value: 'PERSONAL_SAVINGS', label: 'Personal savings' },
  { value: 'BUSINESS_CHECKING', label: 'Business checking' },
  { value: 'BUSINESS_SAVINGS', label: 'Business savings' },
];

/** The only places bank details may be sent. A server-supplied URL anywhere else disables paying. */
export const INTUIT_TOKEN_HOSTS = ['api.intuit.com', 'sandbox.api.intuit.com'];
export function isIntuitTokenUrl(url) {
  return (
    typeof url === 'string' &&
    INTUIT_TOKEN_HOSTS.some((host) => url === 'https://' + host + '/quickbooks/v4/payments/tokens')
  );
}

const TOKENIZE_TIMEOUT_MS = 30000;

const digits = (s) => String(s || '').replace(/\D/g, '');
const phoneError =
  'Enter a 10-digit phone number, with an optional US country code 1 and no extension.';
const phoneDigits = (value) => {
  const raw = String(value || '').trim();
  // Accept ordinary US formatting, but never discard extension text or arbitrary characters.
  if (!/^\+?[\d ().-]+$/.test(raw)) return '';
  const number = digits(raw);
  if (number.length === 11 && number.startsWith('1')) return number.slice(1);
  return number.length === 10 && !raw.startsWith('+') ? number : '';
};

export function validateBank(f) {
  if (!f.name.trim()) return 'Enter the name on the account.';
  if (!/^\d{9}$/.test(digits(f.routing))) return 'The routing number is 9 digits.';
  if (!/^\d{4,17}$/.test(digits(f.account))) return 'Enter the account number.';
  if (digits(f.account) !== digits(f.accountConfirm)) return 'The account numbers do not match.';
  if (!ACCOUNT_TYPES.some((t) => t.value === f.accountType)) return 'Pick the account type.';
  if (!phoneDigits(f.phone)) return phoneError;
  if (!f.authorized) return 'Tick the box to authorize the debit.';
  return '';
}

/** The body Intuit's tokens endpoint takes. Built here so the test can see exactly what leaves the browser. */
export function tokenBody(f) {
  return {
    bankAccount: {
      name: f.name.trim(),
      routingNumber: digits(f.routing),
      accountNumber: digits(f.account),
      accountType: f.accountType,
      phone: phoneDigits(f.phone),
    },
  };
}

const requestId = () =>
  window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : `me-${Date.now()}-${Math.random().toString(16).slice(2)}`;

/** A payer-facing sentence for Intuit's PMT-xxxx codes; never Intuit's developer wording. */
function intuitReason(code, fallback) {
  const c = typeof code === 'string' ? code : '';
  if (/^PMT-4/.test(c))
    return 'Intuit could not accept those details. Please check the routing and account numbers.';
  if (/^PMT-2/.test(c)) return 'The bank account details could not be verified.';
  if (/^PMT-5/.test(c)) return 'The bank account was declined.';
  if (/^PMT-6/.test(c))
    return 'Intuit could not process the details right now. Please try again in a few minutes.';
  return fallback;
}

/**
 * A failure BEFORE anything was sent to our server: the Intuit URL was refused, Intuit could not be
 * reached, or Intuit declined the account details. No payment request exists, so no money can have
 * moved, and the form must stay open for another try - never the "do NOT try again" halt, which
 * is reserved for an outcome our server could not confirm.
 */
const TOKENIZE_FAILURE_MESSAGES = new Set([
  phoneError,
  'Online payments are not available right now (payment endpoint is not Intuit).',
  'Intuit could not be reached to check those details. Nothing was charged. Please try again.',
  'Intuit could not accept those details. Please check the routing and account numbers.',
  'The bank account details could not be verified.',
  'The bank account was declined.',
  'Intuit could not process the details right now. Please try again in a few minutes.',
  'Intuit could not accept those details. Please check them and try again.',
]);
const tokenizeMessages = new WeakMap();
export class TokenizeError extends Error {
  constructor(message) {
    const safeMessage = TOKENIZE_FAILURE_MESSAGES.has(message)
      ? message
      : 'Intuit could not accept those details. Please check them and try again.';
    super(safeMessage);
    this.name = 'TokenizeError';
    tokenizeMessages.set(this, safeMessage);
  }
}

/** Bank details -> Intuit token. Throws a TokenizeError with a readable message. Refuses any non-Intuit URL. */
export async function tokenize(tokenUrl, form) {
  if (!isIntuitTokenUrl(tokenUrl)) {
    throw new TokenizeError(
      'Online payments are not available right now (payment endpoint is not Intuit).'
    );
  }
  if (!phoneDigits(form.phone)) throw new TokenizeError(phoneError);
  let res;
  try {
    res = await fetch(tokenUrl, {
      method: 'POST',
      credentials: 'omit',
      redirect: 'error',
      headers: { 'Content-Type': 'application/json', 'Request-Id': requestId() },
      body: JSON.stringify(tokenBody(form)),
      signal: AbortSignal.timeout ? AbortSignal.timeout(TOKENIZE_TIMEOUT_MS) : undefined,
    });
  } catch (e) {
    throw new TokenizeError(
      'Intuit could not be reached to check those details. Nothing was charged. Please try again.'
    );
  }
  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    body = null;
  }
  if (
    !res.ok ||
    typeof body?.value !== 'string' ||
    !body.value.trim() ||
    body.value.trim() !== body.value ||
    body.value.length > 200 ||
    Array.from(body.value).some(
      (character) => character.charCodeAt(0) <= 32 || character.charCodeAt(0) === 127
    )
  ) {
    const code = body?.errors?.[0]?.code;
    throw new TokenizeError(
      intuitReason(code, 'Intuit could not accept those details. Please check them and try again.')
    );
  }
  return body.value;
}

export function tokenizationErrorMessage(error) {
  return error instanceof TokenizeError ? tokenizeMessages.get(error) || null : null;
}
